/* spring-pdp — quantity stepper, read-more clamp, sticky action bar,
   and self-contained AJAX add-to-cart that hooks the theme's cart drawer.

   Cart-drawer contract (verified against assets/cart-content.js + cart-drawer.js):
     - POST /cart/add.js          → adds the line item
     - GET  /cart.js              → full cart state
     - dispatch "cart:changed"    → drawer refetches its contents
       detail: { cartData: { ...cart, items_added: [{ variant_id, quantity }] } }
     - dispatch "cart:item-added" → drawer scrolls to the new item
     - click [data-action='show-cart-drawer'] → opens the drawer

   Events this element dispatches (both bubble):
     - "spring-pdp:variant"  detail { variant|null, values, source }
                             after every picker- or gallery-driven variant
                             change (see bindVariantSelect) — subscribe here
                             for GTM / sticky bars / future blocks
     - "spring-pdp:slide"    detail { mediaId } — the customer landed the
                             gallery on a slide (internal: drives the picker)
*/
(function () {
  if (customElements.get('spring-pdp')) return;

  class SpringPdp extends HTMLElement {
    connectedCallback() {
      // The quantity <input type="number"> — visible, labelled, and typeable.
      this.qtyInput = this.querySelector('[data-qty-input]');
      this.bindStepper();
      this.bindReadMore();
      this.bindGallery();
      this.bindStickyBar();
      this.bindAddToCart();
      this.bindVariantSelect();
      this.bindEditor();
    }

    /* --- Theme editor: selecting a detail row in the sidebar opens it --- */
    /* The rows share a `name`, so opening one closes the others natively. */
    bindEditor() {
      if (!(window.Shopify && window.Shopify.designMode)) return;
      document.addEventListener('shopify:block:select', (event) => {
        const target = event.target;
        if (!target || !this.contains(target)) return;
        const row = target.closest('details.spring-pdp__detail');
        if (row) row.open = true;
      });
    }

    /* Scroll the gallery to the slide for a media id. Overridden per instance
       by bindGallery() when a multi-image carousel exists; this default keeps
       single-image products (no carousel) a harmless no-op. */
    goToMedia() {
      return false;
    }

    /* --- Variant picker (multi-variant products only) ---
       One control per product option, as in the legacy picker: a <select> of
       that option's values, or — for options listed in the theme's "Swatch
       variant options" — a radio group of swatch chips. The selected values
       resolve to a variant through the [data-variant-data] JSON blob (each
       variant carries its option values, pre-formatted prices and its
       featured media id). A change repaints the groups (the swatch group's
       "Colour: Dune" label; every value that no purchasable variant combines
       with the other groups' current selections is marked sold out) and
       applies the variant: bar price, sub-label (mirrors the picker per the
       Figma), experience duration badge, cart form id, URL ?variant=, the
       booking wizard's target variant (spring-book.js re-reads
       data-variant-id on open and rebuilds its SDK state) and the gallery
       slide (goToMedia). A combination with no variant at all disables the
       CTA as "Unavailable".

       Every applied change is broadcast as 'spring-pdp:variant' (bubbles to
       document) with detail { variant, values, source }: variant is the blob
       entry (id, title, options, available, price, compare, media) or null
       for an unavailable combination; values are the selected option values
       in option order; source is 'picker' or 'gallery'. GTM, the sticky bar
       and future blocks subscribe to that instead of reaching into the
       picker. Nothing is broadcast for the initial server-rendered state.

       The gallery sync runs both ways: picking a variant scrolls the gallery
       to its photo (goToMedia), and the customer landing the gallery on a
       variant's photo selects that variant (the 'spring-pdp:slide' event
       from bindGallery). */
    bindVariantSelect() {
      const picker = this.querySelector('[data-variant-picker]');
      const dataEl = this.querySelector('[data-variant-data]');
      if (!picker || !dataEl) return;
      let variants = [];
      try {
        variants = JSON.parse(dataEl.textContent);
      } catch (err) {
        return;
      }

      // One entry per option group, in option order (matches variant.options).
      // The "Colour: Dune" value span is looked up at paint time, not cached:
      // theme scripts re-render the label paragraph after this element binds
      // (the cached node ends up detached while the radios stay live).
      const groups = Array.from(picker.querySelectorAll('[data-option-group]')).map((el) => ({
        el,
        select: el.querySelector('[data-variant-select]'),
        inputs: Array.from(el.querySelectorAll('[data-variant-swatches] input[type="radio"]')),
      }));
      if (!groups.length) return;

      const priceEl = this.querySelector('[data-pdp-price]');
      const subtitleEl = this.querySelector('[data-variant-subtitle]');
      const badgeEl = this.querySelector('[data-pdp-badge]');
      const idInput = this.querySelector('[data-product-form] input[name="id"]');
      const atcBtn = this.querySelector('[data-add-to-cart]');
      const booking = this.querySelector('spring-booking');
      const labels = {
        cta: (atcBtn && atcBtn.dataset.ctaLabel) || 'Add to bag',
        soldOut: (atcBtn && atcBtn.dataset.soldOutLabel) || 'Sold out',
        unavailable: (atcBtn && atcBtn.dataset.unavailableLabel) || 'Unavailable',
      };

      const setButtonLabel = (btn, label) => {
        const slabs = btn.querySelectorAll('.btn__label');
        if (slabs.length) slabs.forEach((slab) => (slab.textContent = label));
        else btn.textContent = label;
      };
      const setCta = (state) => {
        if (!atcBtn) return;
        atcBtn.disabled = state !== 'available';
        setButtonLabel(atcBtn, state === 'available' ? labels.cta : labels[state]);
      };

      // The selection as the customer sees it: one value per group.
      const selectedValues = () =>
        groups.map((g) => {
          if (g.select) return g.select.value;
          const checked = g.inputs.find((input) => input.checked);
          return checked ? checked.value : '';
        });
      const findVariant = (values) =>
        variants.find((v) => Array.isArray(v.options) && v.options.every((opt, i) => opt === values[i]));
      // Set the controls programmatically (gallery-driven change). No 'change'
      // event fires for that, so it can't loop back into the picker handler.
      const setSelection = (values) => {
        groups.forEach((g, i) => {
          if (g.select) g.select.value = values[i];
          const input = g.inputs.find((el) => el.value === values[i]);
          if (input) input.checked = true;
        });
      };

      // Repaint the groups for a selection: the "Colour: Dune" value label,
      // and a sold-out mark on every value that no variant available for sale
      // combines with the other groups' current values (covers sold-out
      // variants and combinations that don't exist). Values stay selectable
      // so the bar can explain itself (Sold out / Unavailable), matching the
      // dropdown behaviour the Figma shows.
      const paintGroups = (values) => {
        groups.forEach((g, i) => {
          const valueLabel = g.el.querySelector('[data-option-value-label]');
          if (valueLabel) valueLabel.textContent = values[i] || '';
          const controls = g.select ? Array.from(g.select.options) : g.inputs;
          controls.forEach((control) => {
            const candidate = values.slice();
            candidate[i] = control.value;
            const match = findVariant(candidate);
            const soldOut = !match || !match.available;
            const label = control.dataset.label || control.value;
            const text = soldOut ? `${label} — ${labels.soldOut}` : label;
            if (g.select) {
              control.textContent = text;
            } else {
              const chip = control.closest('.spring-pdp__swatch');
              if (chip) chip.classList.toggle('is-soldout', soldOut);
              control.setAttribute('aria-label', text);
            }
          });
        });
      };

      const broadcast = (variant, values, source) => {
        this.dispatchEvent(
          new CustomEvent('spring-pdp:variant', { bubbles: true, detail: { variant, values, source } })
        );
      };

      let currentId = '';

      const applyVariant = (variant, source) => {
        currentId = String(variant.id);
        const values = Array.isArray(variant.options) ? variant.options : selectedValues();
        if (source === 'gallery') setSelection(values);
        paintGroups(values);

        if (priceEl) {
          priceEl.textContent = variant.price;
          if (variant.compare) {
            const compare = document.createElement('s');
            compare.className = 'spring-pdp__price-compare';
            compare.textContent = variant.compare;
            priceEl.append(' ', compare);
          }
        }
        if (subtitleEl) subtitleEl.textContent = variant.title;
        // The media chip mirrors the duration for experiences only (events
        // show dates, products show subtype — both variant-independent).
        if (badgeEl && this.dataset.mode === 'experience') badgeEl.textContent = variant.title;
        if (idInput) idInput.value = variant.id;
        if (booking) booking.dataset.variantId = variant.id;
        // Variants with an image assigned in admin bring their slide into view;
        // variants without one leave the gallery where the customer left it.
        // When the gallery itself made the selection it is already there.
        if (variant.media && source !== 'gallery') this.goToMedia(variant.media);
        setCta(variant.available ? 'available' : 'soldOut');
        // Keep the URL shareable/refresh-stable for the chosen variant.
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
        broadcast(variant, values, source);
      };

      // No variant exists for this combination of values (multi-option
      // products only): keep the customer's picks, disable the CTA.
      const applyUnavailable = (values, source) => {
        currentId = '';
        paintGroups(values);
        setCta('unavailable');
        broadcast(null, values, source);
      };

      picker.addEventListener('change', (event) => {
        const target = event.target;
        if (!target || !target.matches('select, input[type="radio"]')) return;
        if (target.matches('input[type="radio"]') && !target.checked) return;
        const values = selectedValues();
        const variant = findVariant(values);
        if (variant) applyVariant(variant, 'picker');
        else applyUnavailable(values, 'picker');
      });

      // Gallery → picker. bindGallery() announces only customer-driven settles
      // (swipe, thumbnail, progress segment), never the picker's own goToMedia()
      // jump, so the two directions can't ping-pong. A photo shared by several
      // variants selects the first — unless the current variant already shows it.
      this.addEventListener('spring-pdp:slide', (event) => {
        const mediaId = String((event.detail && event.detail.mediaId) || '');
        if (!mediaId) return;
        const current = variants.find((v) => String(v.id) === currentId);
        if (current && String(current.media) === mediaId) return;
        const match = variants.find((v) => String(v.media) === mediaId);
        if (match) applyVariant(match, 'gallery');
      });

      // Initial state is server-rendered; resolve it so the gallery echo
      // guard knows the current variant. Nothing is broadcast for it.
      const initial = findVariant(selectedValues());
      if (initial) currentId = String(initial.id);
    }

    /* --- Image gallery (multi-image products only) --- */
    /* One scroll-snap carousel serves both breakpoints: mobile swipes it,
       desktop thumbnails (and the mobile progress segments) scroll it. Both
       control sets share the same data-gallery-go index and active state. */
    bindGallery() {
      const viewport = this.querySelector('[data-gallery-viewport]');
      if (!viewport) return;
      const slides = Array.from(viewport.querySelectorAll('[data-gallery-slide]'));
      const segments = Array.from(this.querySelectorAll('.spring-pdp__progress-segment'));
      const thumbs = Array.from(this.querySelectorAll('.spring-pdp__thumb'));
      if (slides.length <= 1) return;

      let index = 0;

      const setActive = (next) => {
        if (next === index) return;
        [segments, thumbs].forEach((controls) => {
          if (controls[index]) {
            controls[index].classList.remove('is-active');
            controls[index].removeAttribute('aria-current');
          }
          if (controls[next]) {
            controls[next].classList.add('is-active');
            controls[next].setAttribute('aria-current', 'true');
          }
        });
        index = next;
      };

      // Scroll position → active index (rAF-throttled; slides are 100% wide).
      // Once the scroll settles (150ms without a scroll event) a customer-driven
      // move announces the landed slide's media id — 'spring-pdp:slide' — so the
      // variant picker can follow the gallery. Silent moves (the picker's own
      // goToMedia() jump, the resize re-snap) are skipped so they don't echo.
      // Settling at the end (not per slide) means a smooth scroll that passes
      // other variants' photos on the way selects only the destination.
      let ticking = false;
      let silent = false;
      let settleTimer = 0;
      const settle = () => {
        clearTimeout(settleTimer);
        settleTimer = setTimeout(() => {
          const wasSilent = silent;
          silent = false;
          const slide = slides[index];
          if (wasSilent || !slide || !slide.dataset.mediaId) return;
          this.dispatchEvent(new CustomEvent('spring-pdp:slide', { detail: { mediaId: slide.dataset.mediaId } }));
        }, 150);
      };
      viewport.addEventListener(
        'scroll',
        () => {
          settle();
          if (ticking) return;
          ticking = true;
          requestAnimationFrame(() => {
            ticking = false;
            const next = Math.round(viewport.scrollLeft / viewport.clientWidth);
            setActive(Math.max(0, Math.min(slides.length - 1, next)));
          });
        },
        { passive: true }
      );

      // A touch, wheel or key on the viewport is the customer taking over, even
      // part-way through a silent smooth scroll — that landing counts as theirs.
      ['pointerdown', 'wheel', 'keydown'].forEach((type) => {
        viewport.addEventListener(type, () => (silent = false), { passive: true });
      });

      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const goTo = (next, instant, quiet) => {
        silent = !!quiet;
        viewport.scrollTo({
          left: next * viewport.clientWidth,
          behavior: instant || reduceMotion ? 'auto' : 'smooth',
        });
        // Already at the target → no scroll events → settle here so `silent`
        // still clears for the next customer move.
        settle();
      };
      this.querySelectorAll('[data-gallery-go]').forEach((btn) => {
        btn.addEventListener('click', () => goTo(parseInt(btn.dataset.galleryGo, 10) || 0));
      });

      // Media-id lookup used by the variant picker (and the init jump below).
      // Slides carry data-media-id; the JSON blob carries each variant's
      // featured media id (a number) — compare as strings. Quiet: the picker
      // is driving, so the landing must not re-select a variant.
      this.goToMedia = (mediaId, instant) => {
        const next = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (next < 0) return false;
        goTo(next, instant, true);
        setActive(next);
        return true;
      };

      // Keep the scroll position snapped to the active slide across resizes.
      window.addEventListener(
        'resize',
        () => {
          silent = true;
          viewport.scrollTo({ left: index * viewport.clientWidth, behavior: 'auto' });
          settle();
        },
        { passive: true }
      );

      // Start on the selected variant's image (?variant= deep links). Liquid
      // resolves the id; the jump is instant so there is no visible scroll.
      const initialId = viewport.dataset.initialMediaId;
      if (initialId) requestAnimationFrame(() => this.goToMedia(initialId, true));
    }

    /* --- Quantity stepper --- */
    bindStepper() {
      if (!this.qtyInput) return;
      // The <input type="number"> is the visible, focusable control — users can
      // type a quantity directly; the +/- buttons step the same input. Typed
      // values are clamped on 'change' (blur/Enter), not on every keystroke,
      // so clearing the field mid-edit isn't fought.
      const clampQty = (value) => {
        const min = parseInt(this.qtyInput.min, 10) || 1;
        const max = parseInt(this.qtyInput.max, 10);
        let val = parseInt(value, 10);
        if (!val || val < min) val = min;
        if (max && val > max) val = max;
        return val;
      };
      // Disable (dim) the decrease button once quantity is at the minimum —
      // nothing to step down to, so the '−' reads as disabled at 1.
      const minusBtn = this.querySelector('[data-stepper] [data-step="-1"]');
      const syncMinus = () => {
        if (!minusBtn) return;
        const min = parseInt(this.qtyInput.min, 10) || 1;
        minusBtn.disabled = clampQty(this.qtyInput.value) <= min;
      };
      // Scope to the stepper itself — a broad [data-step] also matches the
      // booking wizard's step sections (data-step="date" etc.).
      this.querySelectorAll('[data-stepper] [data-step]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const delta = parseInt(btn.dataset.step, 10) || 0;
          const current = clampQty(this.qtyInput.value);
          const next = String(clampQty(current + delta));
          this.qtyInput.value = next;
          this.qtyInput.setAttribute('value', next);
          this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
          this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
          syncMinus();
        });
      });
      this.qtyInput.addEventListener('change', () => {
        const next = String(clampQty(this.qtyInput.value));
        if (this.qtyInput.value !== next) this.qtyInput.value = next;
        this.qtyInput.setAttribute('value', next);
        syncMinus();
      });
      syncMinus();
    }

    /* --- Read more (only show the toggle when the text actually overflows) --- */
    bindReadMore() {
      const wrap = this.querySelector('[data-readmore]');
      if (!wrap) return;
      const text = wrap.querySelector('[data-readmore-text]');
      const toggle = wrap.querySelector('[data-readmore-toggle]');
      if (!text || !toggle) return;

      const evaluate = () => {
        if (wrap.classList.contains('is-expanded')) return;
        toggle.hidden = text.scrollHeight - text.clientHeight <= 2;
      };
      requestAnimationFrame(evaluate);
      window.addEventListener('resize', evaluate, { passive: true });

      toggle.addEventListener('click', () => {
        const expanded = wrap.classList.toggle('is-expanded');
        toggle.textContent = expanded ? 'Read less' : 'Read more';
      });
    }

    /* --- Sticky action bar: pin to the viewport bottom once scrolled past --- */
    bindStickyBar() {
      const dock = this.querySelector('[data-bar-dock]');
      const bar = this.querySelector('[data-action-bar]');
      if (!dock || !bar) return;
      // Info-only bars (availability note, no CTA) don't pin to the viewport.
      if (dock.dataset.sticky === 'false') return;

      // Unpin once the footer scrolls into view so the bar never overlaps it.
      const footer = document.querySelector('.spring-footer, footer');

      const update = () => {
        // Stuck once the inline dock position has scrolled above the viewport
        // top — unless the footer has entered the viewport.
        const footerVisible = footer && footer.getBoundingClientRect().top < window.innerHeight;
        const stuck = dock.getBoundingClientRect().top < 0 && !footerVisible;
        if (stuck) dock.style.setProperty('--bar-h', bar.offsetHeight + 'px');
        dock.classList.toggle('is-stuck', stuck);
      };

      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    }

    /* --- AJAX add-to-cart (product mode only) --- */
    bindAddToCart() {
      const form = this.querySelector('[data-product-form]');
      if (!form) return;
      const btn = form.querySelector('[data-add-to-cart]');
      const errorEl = this.querySelector('[data-cart-error]');

      form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (errorEl) errorEl.textContent = '';
        if (btn) {
          btn.classList.add('is-loading');
          btn.setAttribute('aria-busy', 'true');
        }

        try {
          const formData = new FormData(form);
          if (this.qtyInput) formData.set('quantity', this.qtyInput.value || '1');

          const addRes = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { Accept: 'application/json' },
            body: formData,
          });
          const added = await addRes.json();
          if (!addRes.ok) {
            throw new Error(added.description || added.message || 'Could not add to cart.');
          }

          const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
          const addedItems = (added.items || [added]).map((i) => ({
            variant_id: i.variant_id,
            quantity: i.quantity,
          }));

          document.dispatchEvent(
            new CustomEvent('cart:changed', { detail: { cartData: { ...cart, items_added: addedItems } } })
          );
          document.dispatchEvent(new CustomEvent('cart:item-added', { detail: { product: added } }));

          const drawerTrigger = document.querySelector("[data-action='show-cart-drawer']");
          if (drawerTrigger) {
            drawerTrigger.click();
          } else {
            window.location.href = '/cart';
          }
        } catch (err) {
          const message = err.message || 'Could not add to cart.';
          if (errorEl) errorEl.textContent = message;
          // When the add fails because stock ran out (Shopify 422 "... is
          // already sold out."), reflect it on the CTA itself — disabled +
          // "Sold out" — not just in the error line.
          if (btn && /sold out/i.test(message)) {
            btn.disabled = true;
            const label = btn.dataset.soldOutLabel || 'Sold out';
            // The snippet button holds its label in .btn__label slabs (the
            // hover choreography); swap each, falling back to plain text.
            const slabs = btn.querySelectorAll('.btn__label');
            if (slabs.length) slabs.forEach((slab) => (slab.textContent = label));
            else btn.textContent = label;
          }
        } finally {
          if (btn) {
            btn.classList.remove('is-loading');
            btn.removeAttribute('aria-busy');
          }
        }
      });
    }
  }

  customElements.define('spring-pdp', SpringPdp);
})();
